/**
 * Shared option-chain fetch for GEX charts (stable + rtl_legacy).
 */
import { getApiUrl } from "../config/api";
import { fetchStableAccumulatedJson } from "./stableAccumulatedRequest";
import { getGexChainCandidates } from "./symbolPricingService";
import type { OptionRow } from "./gexCalculator";

export interface GexChainFetchResult {
    rows: OptionRow[];
    /** Underlying that returned data (or first candidate tried). */
    usedUnderlying: string;
}

export async function fetchGexChainRows(
    gexUnderlying: string,
): Promise<OptionRow[]> {
    const expiryRange =
        typeof localStorage !== "undefined"
            ? localStorage.getItem("mmec_expiry_range") || "weekly"
            : "weekly";
    const gexQs = `strike_range=all&expiry_range=${encodeURIComponent(expiryRange)}`;
    const urls = [
        getApiUrl(
            `/api/accumulated-data?source=stable&underlyings=${encodeURIComponent(gexUnderlying)}&${gexQs}`,
        ),
        getApiUrl(
            `/api/accumulated-data?source=rtl_legacy&underlyings=${encodeURIComponent(gexUnderlying)}`,
        ),
    ];
    for (const url of urls) {
        try {
            const json = url.includes("source=stable")
                ? await fetchStableAccumulatedJson(url)
                : await (await fetch(url)).json();
            const raw: OptionRow[] = Array.isArray(json)
                ? json
                : json.data || json.rows || json.results || [];
            if (raw.length > 0) return raw;
        } catch {
            /* try next source */
        }
    }
    return [];
}

/**
 * Resolve index aliases (SPX, SPX500, ^SPX) and try SPXW / SPX / ^SPX until chain rows exist.
 */
export async function fetchGexChainForSymbol(
    rawSymbol: string,
): Promise<GexChainFetchResult> {
    const candidates = getGexChainCandidates(rawSymbol);
    if (!candidates.length) {
        const u = String(rawSymbol || "")
            .toUpperCase()
            .trim();
        const rows = await fetchGexChainRows(u);
        return { rows, usedUnderlying: u };
    }

    for (const u of candidates) {
        const rows = await fetchGexChainRows(u);
        if (rows.length > 0) return { rows, usedUnderlying: u };
    }

    if (candidates.length > 1) {
        const combined = candidates.slice(0, 4).join(",");
        const rows = await fetchGexChainRows(combined);
        if (rows.length > 0) {
            return { rows, usedUnderlying: candidates[0] };
        }
    }

    return { rows: [], usedUnderlying: candidates[0] };
}
